import type { ExpressionValue } from '@shared/models/project.model';
import type { QuantityUnit } from '@shared/models/unit.model';
import { isSelectableQuantityUnit } from '@shared/helpers/unit.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { createExpression } from '@shared/helpers/expression-factory.helpers';
import {
  type ExpressionInputChangeEvent,
  type ExpressionInputState,
  classifyExpressionInputChange,
} from '../ExpressionInput/expression-input-change.helpers';

/**
 * Builds an {@link ExpressionInputChangeEvent} from a live panel interaction,
 * normalising the `localUnit` to a {@link SelectableQuantityUnit} (or `undefined`
 * when the unit is not selectable, e.g. internal-only units).
 *
 * Extracted as a pure function so it can be unit-tested independently of the
 * `ExpressionInputPanel` component.
 *
 * @param baselineExpressionValue - The expression value that was active when the
 *   panel opened (the "current" baseline for change detection).
 * @param baselineUnit - The unit that was active when the panel opened.
 * @param newExpressionValue - The new expression value emitted by the panel input.
 * @param localUnit - The unit currently selected inside the panel (may be any
 *   {@link QuantityUnit}, including non-selectable ones).
 */
export const buildPanelChangeEvent = (
  baselineExpressionValue: ExpressionValue | undefined,
  baselineUnit: QuantityUnit | undefined,
  newExpressionValue: ExpressionValue | undefined,
  localUnit: QuantityUnit | undefined,
): ExpressionInputChangeEvent => {
  // Normalise localUnit — non-selectable units (e.g. 'none') are not user-facing
  // and must not be emitted as a unit change.
  const newUnit = isSelectableQuantityUnit(localUnit) ? localUnit : undefined;

  const current: ExpressionInputState = {
    value: baselineExpressionValue?.value,
    expression: baselineExpressionValue?.expression,
    unit: isSelectableQuantityUnit(baselineUnit) ? baselineUnit : undefined,
  };
  const next: ExpressionInputState = {
    value: newExpressionValue?.value,
    expression: newExpressionValue?.expression,
    unit: newUnit,
  };

  return classifyExpressionInputChange(current, next);
};

const PLACEHOLDER_EXPRESSION = '0';

/**
 * Count saved when the formula field is emptied.
 * A manual {@link ExpressionValue.value} is not written back. A real formula is
 * kept without that override; a placeholder formula falls through to
 * {@link fallback} when one exists (DEV-3951).
 */
export const expressionValueAfterClearingPanelField = (
  previous: ExpressionValue | undefined,
  fallback: ExpressionValue | undefined,
): ExpressionValue | undefined => {
  const line = previous?.expression?.trim() ?? '';
  const hasManualValue = guards.is.finiteNumber(previous?.value);
  const isPlaceholder = line.length === 0 || line === PLACEHOLDER_EXPRESSION;

  if (hasManualValue && previous && !isPlaceholder) {
    return { ...previous, value: undefined };
  }
  if (fallback) return undefined;
  if (hasManualValue && previous) return { ...previous, value: undefined };
  return createExpression(0);
};
